import type { Rect } from './types';

/**
 * Geometry for the spotlight — the dimmed page with the highlighted elements
 * punched out of it. See {@link TourSpotlight} for how the two layers it feeds
 * fit together.
 */

/** Inset the spotlight grows around its target, in pixels. */
export const HIGHLIGHT_PADDING = 6;

/**
 * Grows the target rect by the largest uniform padding up to
 * `HIGHLIGHT_PADDING` that keeps the box inside the viewport. A surface flush
 * with a viewport edge — a full-bleed drawer, a full-height sidebar — gets none,
 * so its ring and corner radii are drawn on-screen rather than beyond the edge.
 * Uniform rather than per-side: an inset that differs per side puts the corner
 * arcs somewhere the target has no corner.
 */
export const inflateWithinViewport = (
  rect: Rect,
  vw: number,
  vh: number
): Rect => {
  const padding = Math.max(
    0,
    Math.min(
      HIGHLIGHT_PADDING,
      rect.top,
      rect.left,
      vw - (rect.left + rect.width),
      vh - (rect.top + rect.height)
    )
  );

  return {
    top: rect.top - padding,
    left: rect.left - padding,
    width: rect.width + padding * 2,
    height: rect.height + padding * 2,
  };
};

export type CornerRadii = {
  topLeft: number;
  topRight: number;
  bottomRight: number;
  bottomLeft: number;
};

/** Keeps path coordinates short enough to read in a `clip-path` value. */
const round = (value: number): number => Math.round(value * 100) / 100;

const capRadius = (radius: number, rect: Rect): number =>
  Math.max(0, Math.min(radius, rect.width / 2, rect.height / 2));

/**
 * One **subpath** for `rect`, to be concatenated into a larger `path()` rather
 * than used on its own. Corner radii are capped at half the shorter side, the
 * way CSS scales radii that would otherwise overlap.
 *
 * Every caller uses these as the holes punched in the dimming layer, whose outer
 * viewport rect {@link toDimClipPath} writes separately. Hence the winding: this
 * is drawn **counter-clockwise** from the start of its top-left corner arc,
 * against that outer rect's clockwise winding, so the two cut a hole under
 * either fill rule rather than depending on `evenodd` being honoured.
 */
export const toRoundedRectPath = (rect: Rect, radii: CornerRadii): string => {
  const { top, left, width, height } = rect;
  const right = left + width;
  const bottom = top + height;
  const topLeft = capRadius(radii.topLeft, rect);
  const topRight = capRadius(radii.topRight, rect);
  const bottomRight = capRadius(radii.bottomRight, rect);
  const bottomLeft = capRadius(radii.bottomLeft, rect);

  return [
    `M${round(left + topLeft)},${round(top)}`,
    `A${round(topLeft)},${round(topLeft)} 0 0 0 ${round(left)},${round(top + topLeft)}`,
    `V${round(bottom - bottomLeft)}`,
    `A${round(bottomLeft)},${round(bottomLeft)} 0 0 0 ${round(left + bottomLeft)},${round(bottom)}`,
    `H${round(right - bottomRight)}`,
    `A${round(bottomRight)},${round(bottomRight)} 0 0 0 ${round(right)},${round(bottom - bottomRight)}`,
    `V${round(top + topRight)}`,
    `A${round(topRight)},${round(topRight)} 0 0 0 ${round(right - topRight)},${round(top)}`,
    'Z',
  ].join(' ');
};

export const doRectsOverlap = (a: Rect, b: Rect): boolean =>
  a.left < b.left + b.width &&
  b.left < a.left + a.width &&
  a.top < b.top + b.height &&
  b.top < a.top + a.height;

/** The smallest rect containing both — one hole in place of two overlapping ones. */
export const toBoundingRect = (a: Rect, b: Rect): Rect => {
  const left = Math.min(a.left, b.left);
  const top = Math.min(a.top, b.top);
  return {
    top,
    left,
    width: Math.max(a.left + a.width, b.left + b.width) - left,
    height: Math.max(a.top + a.height, b.top + b.height) - top,
  };
};

/**
 * `clip-path` for the dimming layer: the whole viewport, with `holePaths` cut
 * out of it — which is how a step highlights two elements at once. The outer
 * viewport rect is the `M0,0 …` subpath written here; the holes wind against it
 * (see {@link toRoundedRectPath}) so they read as holes under either fill rule.
 */
export const toDimClipPath = (
  holePaths: string[],
  vw: number,
  vh: number
): string =>
  `path(evenodd, "${[
    `M0,0 H${round(vw)} V${round(vh)} H0 Z`,
    ...holePaths,
  ].join(' ')}")`;
